import { useCartContext } from '@/context/Cart/CartContext';
import { useSnackbarContext } from '@/context/Snackbar/SnackbarContext';
import { useCreateAndPublish } from '@/hooks/cart/useCreateAndPublish';
import { useCreateCart } from '@/hooks/cart/useCreateCart';
import { useGetCartDetails } from '@/hooks/cart/useGetCartDetails';
import { useUpdateAndPublish } from '@/hooks/cart/useUpdateAndPublish';
import { useUpdateCart } from '@/hooks/cart/useUpdateCart';
import { ForwardedRef, useEffect, useImperativeHandle } from 'react';
import { useImmer } from 'use-immer';
import { CloseModalHandler } from '../CartModal';
import { cartDetailApiResponseToCreateEditCart } from '../mapper';
import { initialState } from './CreateEditUiModel';
import { validateInputs } from './inputValidation';
import { useUiUpdaters } from './uiModifications';

export function useCreateEditCartViewController(
  closeModalHandlerRef: ForwardedRef<CloseModalHandler>
) {
  const [uiState, setState] = useImmer(initialState);

  const createCartHook = useCreateCart();
  const createAndPublishCartHook = useCreateAndPublish();
  const updateCartHook = useUpdateCart();
  const updateAndPublishHook = useUpdateAndPublish();
  const uiUpdaters = useUiUpdaters(setState);
  const { showSnackError, showSnackSuccess } = useSnackbarContext();
  const getCartDetailHook = useGetCartDetails();

  const { openModal, setOpenModal, setOpenConfirmDraftOrderModal } =
    useCartContext();

  useImperativeHandle(closeModalHandlerRef, () => ({
    onCloseRequested: () => {
      if (!uiState.products.length) {
        setOpenModal?.(undefined);
        return;
      }
      setOpenConfirmDraftOrderModal?.(true);
    },
  }));

  useEffect(() => {
    if (!openModal) return;
    if (openModal.type === 'edit') {
      getCartDetailHook.setCartId(openModal.cartId);
    }
  }, [openModal]);

  useEffect(() => {
    if (!getCartDetailHook.data) return;
    setState(cartDetailApiResponseToCreateEditCart(getCartDetailHook.data));
  }, [getCartDetailHook.data]);

  const actionHandler = (successMessage: string, errorMessage: string) => ({
    onSuccess: () => {
      showSnackSuccess(successMessage);
      setOpenModal?.(undefined);
    },
    onError: () => showSnackError(errorMessage),
    onSettled: () => {
      setState((prev) => {
        prev.saveCartButton = {
          loading: false,
          disabled: false,
        };
        prev.finishCartButton = {
          loading: false,
          disabled: false,
        };
      });
    },
  });

  const validateInput = ({ isDraft }: { isDraft: boolean }): boolean => {
    const inputValidated = validateInputs(uiState, isDraft);
    if (inputValidated === true) return true;
    else {
      setState(inputValidated);
      return false;
    }
  };

  const onClickedSaveDraft = () => {
    if (!validateInput({ isDraft: true })) return;
    setState((draft) => {
      draft.saveCartButton = { loading: true };
      draft.finishCartButton = { disabled: true };
    });
    if (openModal?.type === 'create') {
      createCartHook.mutate(uiState, actionHandler('Borrador creado', 'error'));
    } else if (openModal?.type === 'edit') {
      updateCartHook.mutate(
        { cartId: openModal.cartId, createEdutCartUi: uiState },
        actionHandler('Borrador actualizado', 'error')
      );
    }
  };

  const onClickedSaveCart = () => {
    if (!validateInput({ isDraft: false })) return;
    setState((draft) => {
      draft.finishCartButton = { loading: true };
      draft.saveCartButton = { disabled: true };
    });
    if (openModal?.type === 'create') {
      createAndPublishCartHook.mutate(
        uiState,
        actionHandler('Carrito publicado', 'error')
      );
    } else if (openModal?.type === 'edit') {
      updateAndPublishHook.mutate(
        { cartId: openModal.cartId, createEditCartUi: uiState },
        actionHandler('Carrito publicado', 'error')
      );
    }
  };

  return {
    uiState: uiState,
    actions: {
      ...uiUpdaters,
      onClickedSaveDraft,
      onClickedSaveCart,
    },
  };
}
